import {SurfaceBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {CompassDir} from '@mintdata/mint-kit/api/models/CompassDir';
import * as classnames from 'classnames/bind';
import {computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {CSSProperties} from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';

const styles = require('./ResizeHandles.less');
const cx = classnames.bind(styles);

export interface ResizeHandlesProps
{
    className?:string;
    borderClassName?:string;
    handleClassName?:string;
    zoom?:number;
    isSelfDirected?:boolean;
    isBoxLocked?:boolean;
    isBoxSelected?:boolean;

    onBoxResizeStart:(direction?:CompassDir) => (void);
    onBoxResize:(boundsDelta:SurfaceBounds, direction?:CompassDir) => (void);
    onBoxResizeStop:() => (void);
}

@observer
export class ResizeHandles extends React.Component<ResizeHandlesProps, any>
{
    public static readonly HANDLE_MARKER_CLASS = '__handleMarker';

    private _handleStart = (dir:CompassDir):any =>
    {
        return (e:MouseEvent) =>
        {
            const {onBoxResizeStart} = this.props;

            onBoxResizeStart(dir);

            e.stopPropagation();
        };
    };

    private _handleDrag = (dir:CompassDir):any =>
    {
        return (e:MouseEvent, data:DraggableData) =>
        {
            e.stopPropagation();

            const {surfaceArea} = controllers.ui;
            surfaceArea.onMouseMoveEvent(e.clientX, e.clientY);

            const {onBoxResize} = this.props;
            const dx = data.deltaX;
            const dy = data.deltaY;
            const fullDx = data.lastX + data.deltaX;
            const fullDy = data.lastY + data.deltaY;

            switch( dir )
            {
                case CompassDir.SouthEast:
                {
                    onBoxResize(SurfaceBounds.from(0, 0, dx, dy), CompassDir.SouthEast);
                    break;
                }
                case CompassDir.SouthWest:
                {
                    onBoxResize(SurfaceBounds.from(fullDx, 0, -fullDx, dy), CompassDir.SouthWest);
                    break;
                }
                case CompassDir.NorthWest:
                {
                    onBoxResize(SurfaceBounds.from(fullDx, fullDy, -fullDx, -fullDy), CompassDir.NorthWest);
                    break;
                }
                case CompassDir.NorthEast:
                {
                    onBoxResize(SurfaceBounds.from(0, fullDy, dx, -fullDy), CompassDir.NorthEast);
                    break;
                }
                case CompassDir.East:
                {
                    onBoxResize(SurfaceBounds.from(0, 0, dx, 0), CompassDir.East);
                    break;
                }
                case CompassDir.South:
                {
                    onBoxResize(SurfaceBounds.from(0, 0, 0, dy), CompassDir.South);
                    break;
                }
                case CompassDir.West:
                {
                    onBoxResize(SurfaceBounds.from(fullDx, 0, -fullDx, 0), CompassDir.West);
                    break;
                }
                case CompassDir.North:
                {
                    onBoxResize(SurfaceBounds.from(0, fullDy, 0, -fullDy), CompassDir.North);
                    break;
                }
            }
        };
    };

    public static isResizerHandle(element:HTMLElement):boolean
    {
        const className = element.className;
        return !!className && className.indexOf(ResizeHandles.HANDLE_MARKER_CLASS) !== -1;
    }

    private _renderHandle(dir:CompassDir)
    {
        const {surfaceBoxReparenting} = controllers.ui;

        const {handleClassName, onBoxResizeStop} = this.props;

        if( !this._shouldRenderHandle )
            return null;

        const className:string = CompassDir[dir];

        return (
            <DraggableCore onStart={this._handleStart(dir)}
                           onDrag={this._handleDrag(dir)}
                           onStop={onBoxResizeStop}
                           enableUserSelectHack={false}
            >
                <div
                    data-resize-handle="true"
                    className={cx(
                        'handleBase',
                        className,
                        ResizeHandles.HANDLE_MARKER_CLASS,
                        handleClassName
                    )}
                    style={this._zoomStyle}
                />
            </DraggableCore>
        );
    }

    render()
    {
        const {className, borderClassName, isBoxSelected} = this.props;

        if( isBoxSelected && this._areMultipleBoxesSelected )
            return null;

        return (
            <div
                data-box-resize-handles="true"
                className={cx('handlesContainer', className)}
            >
                <div
                    data-box-resize-border="true"
                    className={cx('border', borderClassName)}
                    style={this._zoomStyle}
                />
                {isBoxSelected && this._renderHandle(CompassDir.North)}
                {isBoxSelected && this._renderHandle(CompassDir.East)}
                {isBoxSelected && this._renderHandle(CompassDir.South)}
                {isBoxSelected && this._renderHandle(CompassDir.West)}
                {isBoxSelected && this._renderHandle(CompassDir.NorthEast)}
                {isBoxSelected && this._renderHandle(CompassDir.SouthEast)}
                {isBoxSelected && this._renderHandle(CompassDir.SouthWest)}
                {isBoxSelected && this._renderHandle(CompassDir.NorthWest)}
            </div>
        );
    }

    @computed
    private get _zoomStyle():CSSProperties
    {
        const {zoom} = this.props;

        return zoom !== 1 ? {zoom: Math.max(1 / zoom, 0.5)} : null;
    }

    @computed
    private get _areMultipleBoxesSelected():boolean
    {
        const {surfaceSelection} = controllers.ui;

        return surfaceSelection.areMultipleBoxesSelected();
    }

    @computed
    private get _shouldRenderHandle():boolean
    {
        const {surfaceBoxReparenting} = controllers.ui;

        const {
            isSelfDirected,
            isBoxLocked,
            isBoxSelected
        } = this.props;

        return isSelfDirected
            && !isBoxLocked
            && isBoxSelected
            && !surfaceBoxReparenting.isBoxReparentingEnabled();
    }
}
